import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { Footer, Header, PAGE_CONTAINER } from "@/components/site";
import { SITE } from "@/lib/seo-data";
import { useTaal } from "@/lib/taal";

export const Route = createFileRoute("/wat-kost-een-elektricien")({
  head: () => ({
    meta: [
      { title: "Wat kost een elektricien? Richtprijzen per klus | offerto" },
      { name: "description", content: "Bekijk bestaande richtprijzen voor groepenkasten, laadpalen, verlichting, storingen en meer. Vergelijk gratis elektricienoffertes via offerto." },
      { property: "og:title", content: "Wat kost een elektricien? | offerto" },
      { property: "og:description", content: "Vergelijk richtprijzen per elektricienklus en vind een passende offerte." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/wat-kost-een-elektricien` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/wat-kost-een-elektricien` }],
  }),
  component: ElektricienKosten,
});

function ElektricienKosten() {
  const { t } = useTaal();
  const jobs = [
    { t: t("klusGroepenkast"), p: "€ 650 – € 1.400", to: "/elektricien/groepenkast" },
    { t: t("klusLaadpaal"), p: "€ 900 – € 2.000", to: "/elektricien/laadpaal-installeren" },
    { t: t("klusVerlichting"), p: "€ 250 – € 900", to: "/elektricien/verlichting-aanleggen" },
    { t: t("klusStoring"), p: "€ 80 – € 250", to: "/elektricien/storing-verhelpen" },
    { t: t("klusStopcontact"), p: "€ 90 – € 180", to: undefined as string | undefined },
    { t: t("klusHerbedraden"), p: "€ 3.500 – € 8.000", to: undefined as string | undefined },
  ];

  return <div className="min-h-screen bg-background"><Header />
      <main className="bg-secondary py-16">
        <div className={`${PAGE_CONTAINER} grid gap-12 lg:grid-cols-[400px_minmax(0,1fr)] lg:gap-[72px]`}>
          <div>
            <p className="text-[13px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{t("kostenEyebrow")}</p>
            <h1 className="mt-3 text-4xl font-extrabold">{t("kostenTitel")}</h1>
            <p className="mt-4 text-muted-foreground">{t("kostenIntro")}</p>
            <div className="mt-6 flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-hero-accent text-lg font-extrabold text-foreground">%</span>
              <p className="text-sm font-semibold">{t("kostenBespaar")}</p>
            </div>
            <Link
              to="/diensten"
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground transition-opacity hover:opacity-90"
            >
              {t("kostenLink")}
              <ChevronRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 border-b border-border px-6 py-3 text-[13px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
              <span>{t("kostenKolomKlus")}</span>
              <span>{t("kostenKolomPrijs")}</span>
            </div>
            {jobs.map((j) => {
              const inhoud = (
                <>
                  <span className="min-w-0">
                    <span className="block text-[17px] font-semibold">{j.t}</span>
                    <span className="mt-0.5 block text-sm font-bold tabular-nums text-foreground sm:hidden">{j.p}</span>
                  </span>
                  <span className="hidden shrink-0 items-center gap-2 text-[15px] font-bold tabular-nums text-foreground sm:flex">
                    {j.p}
                    {j.to && <ChevronRight className="size-4 text-muted-foreground" aria-hidden="true" />}
                  </span>
                  {j.to && <ChevronRight className="size-4 shrink-0 self-center text-muted-foreground sm:hidden" aria-hidden="true" />}
                </>
              );
              const className = "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-border px-6 py-4 last:border-b-0";
              return j.to ? (
                <Link key={j.t} to={j.to} className={`${className} transition-colors hover:bg-secondary`}>
                  {inhoud}
                </Link>
              ) : (
                <div key={j.t} className={className}>
                  {inhoud}
                </div>
              );
            })}
          </div>
        </div>
      </main>

    <Footer /></div>;
}
